import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { birimListesi } from '../api/organizasyon';
import { oturumDeposuKullan } from '../durum/oturum-deposu';
import type { Kimlik } from '../api/tipler';

/**
 * BİRİM SEÇİCİ.
 *
 * Personel listesindeki açılır kutu üçüncü ekranda tekrarlayınca buraya
 * alındı. Süzgeç mantığı tek yerde: `GET /organizasyon/birim` sayfalama
 * yapmıyor ve altı kademenin tamamı listelenseydi 3.000 personelli kurumda
 * bu kutu yüzlerce satır olurdu.
 *
 * VARSAYILAN K3–K4 (müdürlük ve şeflik). Ekip düzeyine inmek gerekirse
 * `enAltKademe` yükseltilir; vardiya planı ekranı bunu kullanıyor çünkü plan
 * EKİP düzeyinde üretiliyor.
 */
export function BirimSecici({
  deger,
  degisti,
  etiket = 'Birim',
  bosSecenek,
  enUstKademe = 3,
  enAltKademe = 4,
  pasif = false,
  kendiBirimiyleBasla = false,
}: {
  deger: Kimlik | undefined;
  degisti: (yeni: Kimlik | undefined) => void;
  etiket?: string;
  /** Verilirse "tümü" seçeneği eklenir. Zorunlu seçimde BOŞ BIRAKILIR. */
  bosSecenek?: string;
  enUstKademe?: number;
  enAltKademe?: number;
  pasif?: boolean;
  /**
   * Seçim boşken kullanıcının KENDİ biriminden başlar.
   *
   * Birimi zorunlu olan ekranlarda (vardiya planı, görev, rapor) kullanıcı
   * boş kutuyla karşılaşıp listeden rastgele bir birim seçiyor ve kapsamı
   * dışındaki birimde hata alıyordu. Kendi birimi her zaman kapsamındadır.
   *
   * "Tümü" seçeneği olan ekranlarda KAPALI kalır: orada boş seçim anlamlıdır.
   */
  kendiBirimiyleBasla?: boolean;
}) {
  const birimler = useQuery({ queryKey: ['birim', 'liste'], queryFn: () => birimListesi() });

  const etkinBirim = oturumDeposuKullan((d) => d.etkinAtama?.birimId);

  /*
   * BİR KEZ çalışır: kullanıcı sonradan başka birim seçerse geri alınmaz.
   * Bağımlılığa `deger` konulsaydı her temizlemede kendi birimine döner ve
   * kullanıcı seçimini silemezdi.
   */
  const ilkAtamaYapildi = useRef(false);

  useEffect(() => {
    if (!kendiBirimiyleBasla || ilkAtamaYapildi.current) return;
    if (deger !== undefined || etkinBirim === undefined) return;
    if (!secilebilirMi(etkinBirim)) return;

    ilkAtamaYapildi.current = true;
    degisti(etkinBirim);
  });

  const secenekler = (birimler.data ?? []).filter(
    (b) => b.kademeSeviye >= enUstKademe && b.kademeSeviye <= enAltKademe,
  );

  // Kendi birimi kademe süzgecinin dışında kalabilir (örn. başkan yardımcısı);
  // listede olmayan bir değer seçilirse kutu boş görünür.
  function secilebilirMi(birimId: string): boolean {
    return secenekler.some((b) => b.birimId === birimId);
  }

  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-slate-700">{etiket}</span>
      <select
        value={deger ?? ''}
        disabled={pasif || birimler.isPending}
        onChange={(olay) => degisti(olay.target.value === '' ? undefined : olay.target.value)}
        className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-kurum-500 focus:ring-2 focus:ring-kurum-100 disabled:bg-slate-50 disabled:text-slate-400"
      >
        {/*
          ZORUNLU SEÇİMDE BOŞ SEÇENEK YOK ama bir yer tutucu gerekiyor:
          aksi halde kutu ilk birimi seçili gösterir ve kullanıcı hiç
          seçim yapmadan "seçtim" sanır.
        */}
        {bosSecenek === undefined ? (
          deger === undefined && <option value="">Birim seçin…</option>
        ) : (
          <option value="">{bosSecenek}</option>
        )}

        {secenekler.map((b) => (
          <option key={b.birimId} value={b.birimId}>
            {b.ad}
          </option>
        ))}
      </select>

      {birimler.isError && (
        <span className="mt-1 block text-xs text-red-600">Birimler yüklenemedi.</span>
      )}
      {!birimler.isPending && !birimler.isError && secenekler.length === 0 && (
        <span className="mt-1 block text-xs text-amber-700">
          Kapsamınızda bu kademede birim yok.
        </span>
      )}
    </label>
  );
}
